import { useQuery } from '@tanstack/react-query';
import { useParams, Navigate, Link, useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { useAuthStore } from '../store/authStore';
import MentionText from '../components/MentionText';

interface UserActivityDetails {
  user: {
    userId: string;
    name: string;
    email: string;
  };
  photos: Array<{
    photoId: string;
    fileName: string;
    imageUrl: string;
    thumbnailUrls: {
      small: string;
      medium: string;
      large: string;
    };
    createdAt: string;
    likeCount: number;
    commentCount: number;
  }>;
  comments: Array<{
    commentId: string;
    content: string;
    createdAt: string;
    photo: {
      photoId: string;
      fileName: string;
      imageUrl: string;
      thumbnailUrls: {
        small: string;
        medium: string;
        large: string;
      };
      ownerName: string;
    };
  }>;
  likes: Array<{
    photoId: string;
    createdAt: string;
    photo: {
      photoId: string;
      fileName: string;
      imageUrl: string;
      thumbnailUrls: {
        small: string;
        medium: string;
        large: string;
      };
      ownerName: string;
    };
  }>;
  audioClips: Array<{
    messageId: string;
    duration: number;
    createdAt: string;
    photo: {
      photoId: string;
      fileName: string;
      imageUrl: string;
      thumbnailUrls: {
        small: string;
        medium: string;
        large: string;
      };
      ownerName: string;
    };
  }>;
  summary: {
    totalPhotos: number;
    totalComments: number;
    totalLikes: number;
    totalAudioClips: number;
    totalActivity: number;
  };
}

export default function UserActivityPage() {
  const { userId } = useParams<{ userId: string }>();
  const { isSuperAdmin } = useAuthStore();
  const navigate = useNavigate();

  // Only superadmins can access this page
  if (!isSuperAdmin) {
    return <Navigate to="/feed" replace />;
  }

  const { data: activity, isLoading, error } = useQuery<UserActivityDetails>({
    queryKey: ['userActivity', userId],
    queryFn: () => api.getUserActivity(userId!),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-500 border-t-transparent mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading user activity...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !activity) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <h2 className="text-2xl font-bold text-red-900 mb-2">Error Loading Activity</h2>
            <p className="text-red-700">{(error as Error)?.message || 'User not found'}</p>
            <Link to="/statistics" className="mt-4 inline-block text-primary-600 hover:text-primary-700 underline">
              ← Back to Statistics
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header with Back Button */}
        <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8">
          <button
            onClick={() => navigate('/statistics')}
            className="text-gray-600 hover:text-gray-900 mb-4 flex items-center gap-2 transition-colors"
          >
            <span>←</span>
            <span>Back to Statistics</span>
          </button>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-accent-500 rounded-2xl flex items-center justify-center">
              <span className="text-3xl">👤</span>
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900">
                {activity.user.name}
              </h1>
              <p className="text-gray-600 mt-1">{activity.user.email}</p>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <SummaryCard icon="📸" label="Photos Uploaded" value={activity.summary?.totalPhotos || 0} color="from-blue-500 to-blue-600" />
          <SummaryCard icon="💬" label="Comments Made" value={activity.summary?.totalComments || 0} color="from-purple-500 to-purple-600" />
          <SummaryCard icon="❤️" label="Likes Given" value={activity.summary?.totalLikes || 0} color="from-pink-500 to-pink-600" />
          <SummaryCard icon="🎤" label="Audio Clips" value={activity.summary?.totalAudioClips || 0} color="from-green-500 to-green-600" />
          <SummaryCard icon="✨" label="Total Activity" value={activity.summary?.totalActivity || 0} color="from-orange-500 to-orange-600" />
        </div>

        {/* Comments Made */}
        {(activity.comments?.length || 0) > 0 && (
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <span>💬</span>
              Comments Made ({activity.comments.length})
            </h2>
            <div className="space-y-4">
              {activity.comments.map((comment) => (
                <div key={comment.commentId} className="border-l-4 border-purple-500 bg-gray-50 p-4 rounded-r-lg hover:bg-purple-50 transition-colors">
                  <div className="flex items-start gap-4">
                    <Link to={`/feed?photoId=${comment.photo.photoId}`}>
                      <img 
                        src={comment.photo.thumbnailUrls.small} 
                        alt={comment.photo.fileName}
                        className="w-16 h-16 object-cover rounded-lg hover:opacity-80 transition-opacity"
                      />
                    </Link>
                    <div className="flex-1">
                      <p className="text-sm text-gray-600 mb-1">
                        Commented on{' '}
                        <Link 
                          to={`/feed?photoId=${comment.photo.photoId}`}
                          className="text-primary-600 hover:text-primary-700 font-medium underline"
                          title={`Photo by ${comment.photo.ownerName}`}
                        >
                          {comment.photo.fileName}
                        </Link>
                        {' '}by {comment.photo.ownerName}
                      </p>
                      <p className="text-gray-900 italic mb-2">
                        "<MentionText value={comment.content} />"
                      </p>
                      <span className="text-xs text-gray-500">{formatDate(comment.createdAt)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Likes Given */}
        {(activity.likes?.length || 0) > 0 && (
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <span>❤️</span>
              Likes Given ({activity.likes.length})
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {activity.likes.map((like, idx) => (
                <Link 
                  key={`${like.photoId}-${idx}`}
                  to={`/feed?photoId=${like.photoId}`}
                  className="border border-gray-200 rounded-lg p-3 hover:border-pink-300 hover:bg-pink-50 transition-all group"
                >
                  <img 
                    src={like.photo.thumbnailUrls.small} 
                    alt={like.photo.fileName}
                    className="w-full h-32 object-cover rounded-lg mb-3 group-hover:opacity-90 transition-opacity"
                  />
                  <div className="flex items-start gap-2">
                    <span className="text-pink-500 text-lg flex-shrink-0">❤️</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-900 font-medium truncate group-hover:text-primary-600">{like.photo.fileName}</p>
                      <p className="text-xs text-gray-500">
                        by {like.photo.ownerName}
                      </p>
                      <p className="text-xs text-gray-400">{formatDate(like.createdAt)}</p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Audio Clips */}
        {(activity.audioClips?.length || 0) > 0 && (
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <span>🎤</span>
              Audio Clips ({activity.audioClips.length})
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {activity.audioClips.map((clip, idx) => (
                <Link 
                  key={`${clip.messageId}-${idx}`}
                  to={`/feed?photoId=${clip.photo.photoId}`}
                  className="border border-gray-200 rounded-lg p-3 hover:border-green-300 hover:bg-green-50 transition-all group"
                >
                  <img 
                    src={clip.photo.thumbnailUrls.small} 
                    alt={clip.photo.fileName}
                    className="w-full h-32 object-cover rounded-lg mb-3 group-hover:opacity-90 transition-opacity"
                  />
                  <div className="flex items-start gap-2">
                    <span className="text-green-500 text-lg flex-shrink-0">🎤</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-900 font-medium truncate group-hover:text-primary-600">{clip.photo.fileName}</p>
                      <p className="text-xs text-gray-500">
                        by {clip.photo.ownerName}
                      </p>
                      <p className="text-xs text-gray-400">{clip.duration}s • {formatDate(clip.createdAt)}</p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Photos Uploaded */}
        {(activity.photos?.length || 0) > 0 && (
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <span>📸</span>
              Photos Uploaded ({activity.photos.length})
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Photo</th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Likes</th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Comments</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Uploaded</th>
                  </tr>
                </thead>
                <tbody>
                  {activity.photos.map((photo) => (
                    <tr key={photo.photoId} className="border-b border-gray-100 hover:bg-primary-50 transition-colors">
                      <td className="py-3 px-4">
                        <Link 
                          to={`/feed?photoId=${photo.photoId}`}
                          className="flex items-center gap-3 group"
                        >
                          <img 
                            src={photo.thumbnailUrls.small} 
                            alt={photo.fileName}
                            className="w-12 h-12 object-cover rounded-lg"
                          />
                          <span className="text-sm text-gray-900 group-hover:text-primary-600 truncate max-w-xs font-medium hover:underline">{photo.fileName}</span>
                        </Link>
                      </td>
                      <td className="py-3 px-4 text-center text-sm">
                        <span className="text-pink-600 font-medium">{photo.likeCount}</span>
                      </td>
                      <td className="py-3 px-4 text-center text-sm">
                        <span className="text-purple-600 font-medium">{photo.commentCount}</span>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600">{formatDate(photo.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* No Activity Message */}
        {(activity.summary?.totalActivity || 0) === 0 && (
          <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
            <div className="text-6xl mb-4">😴</div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">No Activity Yet</h3>
            <p className="text-gray-600">This user hasn't uploaded photos, commented, liked anything, or recorded audio clips yet.</p>
          </div>
        )}
      </div>
    </div>
  );
}

// Helper Component
function SummaryCard({ icon, label, value, color }: { icon: string; label: string; value: number; color: string }) {
  return (
    <div className="bg-white rounded-xl shadow-lg p-4 hover:shadow-xl transition-shadow">
      <div className={`w-10 h-10 bg-gradient-to-br ${color} rounded-lg flex items-center justify-center mb-2 text-xl`}>
        {icon}
      </div>
      <div className="text-2xl font-bold text-gray-900">{value.toLocaleString()}</div>
      <div className="text-sm text-gray-600 mt-1">{label}</div>
    </div>
  );
}

